import React, { useCallback, useEffect, useState } from "react";
import api, { describeAxiosError } from "@/lib/api";
import { toast } from "sonner";
import {
    Shield,
    Users,
    Warning,
    Check,
    Plus,
    X,
    Lock,
    PaperPlaneTilt,
} from "@phosphor-icons/react";

/**
 * PartnerAccessSettings — V2.1.1 iter179.
 *
 * Admin/Superadmin page to control what pre-CV CRM leads are exposed
 * to freelance partners in the workspace. Backed by
 * `/api/workspace/owner-settings` and enforced by
 * `_lead_visible_to_partner` on every list + claim call.
 *
 * Default posture: **DISABLED** — new workspaces don't leak leads to
 * partners until admin explicitly opts in.
 */
export default function PartnerAccessSettings() {
    const [settings, setSettings] = useState(null);
    const [claims, setClaims] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newSource, setNewSource] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, c] = await Promise.all([
                api.get("/workspace/owner-settings"),
                api.get("/admin/partner-claim-events?limit=25"),
            ]);
            setSettings(s.data);
            setClaims(c.data);
        } catch (e) {
            toast.error(describeAxiosError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const patch = async (update) => {
        setSaving(true);
        try {
            const r = await api.patch("/workspace/owner-settings", update);
            setSettings(r.data);
            toast.success("Saved");
        } catch (e) {
            toast.error(describeAxiosError(e));
        } finally {
            setSaving(false);
        }
    };

    const addSource = () => {
        const v = newSource.trim();
        if (!v) return;
        const cur = settings.partner_lead_source_allow || [];
        if (cur.includes(v)) { setNewSource(""); return; }
        patch({ partner_lead_source_allow: [...cur, v] });
        setNewSource("");
    };
    const removeSource = (s) => {
        const cur = (settings.partner_lead_source_allow || []).filter((x) => x !== s);
        patch({ partner_lead_source_allow: cur });
    };

    if (loading || !settings) {
        return <div className="p-8 text-sm text-[#A3A3A3]">Loading…</div>;
    }

    const enabled = !!settings.partner_access_enabled;
    const allowList = settings.partner_lead_source_allow || [];
    const outreachOn = !!settings.outreach_enabled;

    return (
        <div className="px-4 sm:px-8 py-8 max-w-4xl mx-auto" data-testid="partner-access-page">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-2 inline-flex items-center gap-1">
                <Shield size={12} weight="fill" /> Workspace settings
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink mb-2">
                Partner CRM Access
            </h1>
            <p className="text-sm text-[#525252] mb-8 max-w-lg">
                Control which pre-CV leads your workspace exposes to freelance
                partners in their claim pool. Off by default — enable and
                whitelist specific source batches when you're ready.
            </p>

            {/* Master toggle */}
            <div
                className={`border-2 p-5 mb-6 flex items-start gap-4 transition ${
                    enabled ? "border-emerald-500 bg-emerald-50/50" : "border-neutral-300 bg-neutral-50"
                }`}
                data-testid="partner-access-toggle-row"
            >
                <div className="mt-0.5">
                    {enabled
                        ? <Check size={24} weight="bold" className="text-emerald-600" />
                        : <Lock size={24} weight="bold" className="text-neutral-500" />}
                </div>
                <div className="flex-1">
                    <p className="font-heading text-lg font-black tracking-tight text-ink">
                        {enabled ? "Partners CAN see whitelisted leads" : "Partner access is disabled"}
                    </p>
                    <p className="text-sm text-[#525252] mt-1">
                        {enabled
                            ? "Only leads whose `source_batch` matches an entry below are visible to partners."
                            : "Turn this on to let partners claim leads from your allowlisted source batches."}
                    </p>
                </div>
                <button
                    onClick={() => patch({ partner_access_enabled: !enabled })}
                    disabled={saving}
                    data-testid="partner-access-master-toggle"
                    className={`px-4 py-2 text-xs font-black uppercase tracking-wider transition ${
                        enabled
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : "bg-ink text-white hover:bg-cobalt"
                    } disabled:opacity-60`}
                >
                    {enabled ? "Disable access" : "Enable access"}
                </button>
            </div>

            {/* V2.1.1 iter180 — CRM Outreach Orchestration kill-switch */}
            <div
                className={`border-2 p-5 mb-6 flex items-start gap-4 transition ${
                    outreachOn ? "border-cobalt bg-cobalt/5" : "border-neutral-300 bg-neutral-50"
                }`}
                data-testid="outreach-toggle-row"
            >
                <div className="mt-0.5">
                    <PaperPlaneTilt
                        size={24}
                        weight={outreachOn ? "fill" : "regular"}
                        className={outreachOn ? "text-cobalt" : "text-neutral-500"}
                    />
                </div>
                <div className="flex-1">
                    <p className="font-heading text-lg font-black tracking-tight text-ink">
                        {outreachOn ? "CRM Outreach drip is ACTIVE" : "CRM Outreach drip is OFF"}
                    </p>
                    <p className="text-sm text-[#525252] mt-1">
                        {outreachOn
                            ? "Unpromoted CRM leads get an automated email + WhatsApp nudge on day 3 and day 7. Idempotent per lead + capped at 500/day/workspace."
                            : "Turn this on to auto-nudge stale leads with a magic-link CTA. Respects DNC, opt-outs and cooldowns."}
                    </p>
                </div>
                <button
                    onClick={() => patch({ outreach_enabled: !outreachOn })}
                    disabled={saving}
                    data-testid="outreach-master-toggle"
                    className={`px-4 py-2 text-xs font-black uppercase tracking-wider transition ${
                        outreachOn
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : "bg-ink text-white hover:bg-cobalt"
                    } disabled:opacity-60`}
                >
                    {outreachOn ? "Stop drip" : "Start drip"}
                </button>
            </div>

            {/* Caps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <CapCard
                    label="Daily claim cap"
                    hint="Per partner"
                    value={settings.partner_daily_cap}
                    onSave={(v) => patch({ partner_daily_cap: v })}
                    max={500}
                    testid="cap-daily"
                />
                <CapCard
                    label="Active claim cap"
                    hint="Total open leads owned"
                    value={settings.partner_active_cap}
                    onSave={(v) => patch({ partner_active_cap: v })}
                    max={5000}
                    testid="cap-active"
                />
                <CapCard
                    label="Min years experience"
                    hint="Hide leads below this"
                    value={settings.partner_lead_min_years}
                    onSave={(v) => patch({ partner_lead_min_years: v })}
                    max={30}
                    step={0.5}
                    testid="cap-years"
                />
            </div>

            {/* Source allowlist */}
            <div className="bg-white border border-[#E5E5E5] p-5 mb-6">
                <h2 className="font-heading text-lg font-black tracking-tight text-ink mb-1">
                    Source batch allowlist
                </h2>
                <p className="text-xs text-[#525252] mb-4">
                    Partners only see leads whose <code className="text-ink font-mono">source_batch</code> starts
                    with (or exactly matches) an entry here. Common values:{" "}
                    <code className="text-ink font-mono">ad_landing</code>,{" "}
                    <code className="text-ink font-mono">bulk_import</code>,{" "}
                    <code className="text-ink font-mono">manual</code>.
                </p>

                {allowList.length === 0 ? (
                    <p className="text-sm text-orange-700 bg-orange-50 border border-orange-200 px-3 py-2 mb-4 inline-flex items-center gap-1">
                        <Warning size={14} weight="fill" /> No sources whitelisted — partners see nothing (safe default).
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {allowList.map((s) => (
                            <span
                                key={s}
                                data-testid={`source-chip-${s}`}
                                className="inline-flex items-center gap-1 bg-cobalt/10 text-cobalt text-xs font-black uppercase tracking-wider px-2 py-1"
                            >
                                {s}
                                <button onClick={() => removeSource(s)} data-testid={`source-remove-${s}`}>
                                    <X size={12} weight="bold" className="ml-1 hover:text-red-600" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                <div className="flex gap-2">
                    <input
                        data-testid="source-input"
                        value={newSource}
                        onChange={(e) => setNewSource(e.target.value)}
                        placeholder="e.g. ad_landing or bulk_import:2024"
                        className="flex-1 border border-[#E5E5E5] focus:border-ink px-3 py-2 text-sm outline-none"
                        onKeyDown={(e) => e.key === "Enter" && addSource()}
                    />
                    <button
                        onClick={addSource}
                        disabled={!newSource.trim()}
                        data-testid="source-add"
                        className="inline-flex items-center gap-1 bg-ink text-white px-3 py-2 text-xs font-black uppercase tracking-wider hover:bg-cobalt transition disabled:opacity-60"
                    >
                        <Plus size={12} weight="bold" /> Add
                    </button>
                </div>
            </div>

            {/* Claim audit */}
            {claims && (
                <div className="bg-white border border-[#E5E5E5] p-5">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-heading text-lg font-black tracking-tight text-ink">
                            Recent claim activity
                        </h2>
                        <span className="text-xs font-bold text-cobalt">
                            <Users size={12} weight="bold" className="inline mr-1" />
                            {claims.total} total · {claims.converted} converted ({Math.round(claims.conversion_rate * 100)}%)
                        </span>
                    </div>
                    {claims.rows.length === 0 ? (
                        <p className="text-sm text-[#A3A3A3]">No partner claims yet.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-[10px] font-black uppercase tracking-wider text-[#525252] border-b border-[#E5E5E5]">
                                <tr>
                                    <th className="text-left pb-2">When</th>
                                    <th className="text-left pb-2">Partner</th>
                                    <th className="text-left pb-2">Source</th>
                                    <th className="text-left pb-2">Converted?</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#F5F5F5]">
                                {claims.rows.map((c) => (
                                    <tr key={c.id} data-testid={`claim-row-${c.id}`}>
                                        <td className="py-2 text-xs text-[#525252] font-mono">
                                            {new Date(c.claimed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                        </td>
                                        <td className="py-2 text-xs text-ink">{c.partner_email || c.partner_id?.slice(0, 8)}</td>
                                        <td className="py-2 text-xs text-[#525252]">{c.source_batch || "—"}</td>
                                        <td className="py-2">
                                            {c.converted_to_candidate_id ? (
                                                <span className="text-[10px] font-black uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5">
                                                    <Check size={10} weight="bold" className="inline mr-0.5" />
                                                    Registered
                                                </span>
                                            ) : (
                                                <span className="text-[10px] font-black uppercase bg-neutral-100 text-neutral-600 px-1.5 py-0.5">
                                                    Open
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

function CapCard({ label, hint, value, onSave, max = 100, step = 1, testid }) {
    const [v, setV] = useState(String(value ?? 0));
    useEffect(() => setV(String(value ?? 0)), [value]);
    const commit = () => {
        const n = Number(v);
        if (!isNaN(n) && n !== value) onSave(n);
    };
    return (
        <div className="bg-white border border-[#E5E5E5] p-4">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">{label}</p>
            <p className="text-[11px] text-[#A3A3A3] mb-2">{hint}</p>
            <input
                data-testid={testid}
                type="number"
                min={0}
                max={max}
                step={step}
                value={v}
                onChange={(e) => setV(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => e.key === "Enter" && commit()}
                className="w-full border border-[#E5E5E5] focus:border-ink px-2 py-1 font-heading text-2xl font-black tracking-tighter text-ink outline-none"
            />
        </div>
    );
}
